import { Download, ExternalLink, Clock, Sparkles, Copy } from "lucide-react";
import { mediaUrl } from "@/lib/academy";
import { FILE_META, SECTION_META, downloadHref } from "@/lib/marketing";
import { PngDownloadLink } from "@/components/marketing/PngDownloadLink";

export const AssetCard = ({ asset, index, onPersonalize, onEmail }) => {
  const sec = SECTION_META[asset.category] || SECTION_META["Sales Materials"];
  const meta = asset.email_body ? { label: asset.channel && asset.channel !== "email" ? "SCRIPT" : "EMAIL" } : FILE_META[asset.file_type];
  const Icon = FILE_META[asset.file_type]?.icon || sec.icon;
  const href = downloadHref(asset);
  const canva = asset.file_type === "canva";
  return (
    <div
      className="group rounded-2xl border border-border bg-card overflow-hidden flex flex-col transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-primary/50 hover:shadow-xl animate-in fade-in slide-in-from-bottom-2"
      style={{ animationDelay: `${index * 50}ms`, animationFillMode: "both" }}
      data-testid={`marketing-asset-card-${asset.id}`}
    >
      <div className={`relative aspect-[16/9] bg-gradient-to-br ${sec.tint} flex items-center justify-center overflow-hidden`}>
        {asset.thumbnail_url ? (
          <img src={mediaUrl(asset.thumbnail_url)} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
        ) : (
          <Icon className="w-12 h-12 transition-transform duration-300 group-hover:scale-110" strokeWidth={1.5} />
        )}
        {asset.personalize && (
          <span className="absolute top-2 left-2 text-[10px] font-semibold rounded-full bg-primary text-primary-foreground px-2 py-0.5 flex items-center gap-1" data-testid={`marketing-asset-personalized-badge-${asset.id}`}><Sparkles className="w-3 h-3" />Personalized</span>
        )}
        {meta && <span className="absolute top-2 right-2 text-[10px] font-mono font-semibold tracking-wider rounded-full bg-black/60 text-white px-2 py-0.5" data-testid={`marketing-asset-type-${asset.id}`}>{meta.label}</span>}
      </div>
      <div className="p-4 flex flex-col flex-1 gap-3">
        <div className="space-y-1 flex-1">
          <div className="font-heading font-semibold leading-snug" data-testid={`marketing-asset-title-${asset.id}`}>{asset.title}</div>
          <p className="text-sm text-muted-foreground line-clamp-3">{asset.description}</p>
        </div>
        {asset.email_body && (
          <button type="button" onClick={() => onEmail(asset)} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold transition-transform duration-200 hover:scale-[1.02]" data-testid={`marketing-asset-email-${asset.id}`}>
            <Copy className="w-4 h-4" />{asset.channel && asset.channel !== "email" ? "Use this script" : "Use this email"}
          </button>
        )}
        {asset.email_body && href && (
          <a href={href} download={asset.file_name || true} className="block text-center text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline" data-testid={`marketing-asset-download-${asset.id}`}>
            Download Word file
          </a>
        )}
        {asset.personalize && (
          <div className="space-y-1.5">
            <button type="button" onClick={() => onPersonalize(asset)} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold transition-transform duration-200 hover:scale-[1.02]" data-testid={`marketing-asset-personalize-${asset.id}`}>
              <Sparkles className="w-4 h-4" />Personalize & download
            </button>
            <a href={href} download={asset.file_name || true} className="block text-center text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline" data-testid={`marketing-asset-download-${asset.id}`}>Download original</a>
            <PngDownloadLink asset={asset} className="w-full" label="Download original as PNG (for print shops)" />
          </div>
        )}
        {asset.personalize || asset.email_body ? null : href ? (
          <a href={href} target={canva ? "_blank" : undefined} rel="noopener noreferrer" download={canva ? undefined : (asset.file_name || true)}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold transition-transform duration-200 hover:scale-[1.02]"
            data-testid={`marketing-asset-download-${asset.id}`}>
            {canva ? <><ExternalLink className="w-4 h-4" />Open in Canva</> : <><Download className="w-4 h-4" />Download</>}
          </a>
        ) : null}
        {!asset.personalize && !asset.email_body && href && <PngDownloadLink asset={asset} className="w-full" />}
        {asset.personalize || asset.email_body || href ? null : (
          <button type="button" disabled className="inline-flex items-center justify-center gap-2 rounded-full border border-border text-muted-foreground px-4 py-2 text-sm font-medium cursor-not-allowed opacity-70" data-testid={`marketing-asset-coming-soon-${asset.id}`}>
            <Clock className="w-4 h-4" />Coming soon
          </button>
        )}
      </div>
    </div>
  );
};
